<script setup lang="ts">
import type { InspectorAssignment, InspectorRowView } from '../types'
import imgTimer from '@/assets/timer@2x.png'
import LoadingIndicator from '@/components/LoadingIndicator.vue'
import { formatAssignmentOrderId, assignmentPinMaskStyle, inspectorDotColor } from '../utils/scheduleInspectorRows'

withDefaults(
  defineProps<{
    rows: InspectorRowView[]
    embedded: boolean
    isRescheduleMode: boolean
    loading?: boolean
  }>(),
  { loading: false },
)

const emit = defineEmits<{
  'toggle-inspector': [inspectorId: string]
  'select-assignment': [payload: { orderId: string; inspectorId: string; assignment: InspectorAssignment }]
}>()
</script>

<template>
  <aside
    class="schedule-panel schedule-panel--inspectors"
    :class="{
      'schedule-ctx--embedded': embedded,
      'schedule-ctx--reschedule': isRescheduleMode,
    }"
  >
    <h2 class="panel-heading">Inspectors</h2>
    <div class="inspector-body" :aria-busy="loading || undefined">
      <div v-if="loading" class="inspector-body-loading" aria-live="polite">
        <LoadingIndicator min-height="120px" />
      </div>
      <ul
        v-if="rows.length"
        class="inspector-list"
        :class="{ 'inspector-list--loading': loading }"
      >
      <li v-for="row in rows" :key="row.inspector.id" class="inspector-block">
        <button
          type="button"
          class="inspector-row"
          :class="{ 'inspector-row--active': row.rowActive }"
          :disabled="!row.selectable"
          @click="emit('toggle-inspector', row.inspector.id)"
        >
          <img
            :src="row.chevronIcon"
            class="inspector-chevron-ic"
            :class="{ open: row.expanded }"
            alt=""
            aria-hidden="true"
          />
          <span
            class="inspector-dot"
            style="background-color: #cccccc"
            aria-hidden="true"
          />
          <span class="inspector-text">
            <span class="inspector-name" :title="row.inspector.name">{{ row.inspector.name }}</span>
            <span class="inspector-orders">
              <span class="inspector-orders-count">{{ row.inspector.orderCount }}</span>
              Order{{ row.inspector.orderCount === 1 ? '' : 's' }}
            </span>
          </span>
        </button>
        <ul v-if="row.expanded && row.assignments.length" class="inspector-assignments">
          <li
            v-for="item in row.assignments"
            :key="`${row.inspector.id}-${item.index}`"
            class="inspector-assignment"
            :class="{
              'inspector-assignment--active': item.active,
              'inspector-assignment--readonly': item.assignment.readonly,
            }"
          >
            <button
              type="button"
              class="inspector-assignment-btn"
              :disabled="!item.clickable"
              @click.stop="
                emit('select-assignment', {
                  orderId: item.assignment.orderId,
                  inspectorId: row.inspector.id,
                  assignment: item.assignment,
                })
              "
            >
              <span class="assignment-idx">{{ item.index + 1 }}</span>
              <span
                class="assignment-pin"
                :style="assignmentPinMaskStyle(inspectorDotColor(row.inspector.orderCount, item.active))"
                aria-hidden="true"
              />
              <span class="assignment-body">
                <span class="assignment-addr">{{ item.assignment.address }}</span>
                <span class="assignment-id">{{ formatAssignmentOrderId(item.assignment.orderId) }}</span>
                <span v-if="item.assignment.timeLabel" class="assignment-time">
                  <img :src="imgTimer" class="assignment-time-ic" alt="" aria-hidden="true" />
                  {{ item.formattedTime }}
                </span>
              </span>
            </button>
          </li>
        </ul>
      </li>
    </ul>
    </div>
  </aside>
</template>

<style scoped>
.schedule-panel--inspectors.schedule-ctx--embedded {
  align-self: start;
  display: flex;
  flex-direction: column;
  width: 252px;
  min-width: 252px;
  max-width: 252px;
  height: 638px;
  min-height: 638px;
  max-height: 638px;
  padding: 0;
  background: #484848;
  box-sizing: border-box;
  overflow: hidden;
}

.schedule-panel--inspectors.schedule-ctx--embedded > .panel-heading {
  flex-shrink: 0;
  box-sizing: border-box;
  width: 72px;
  height: 20px;
  margin: 16px 165px 0 15px;
  padding: 0;
  background: #484848;
  font-family: SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui, sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: 0;
  color: #ffffff;
  text-align: left;
  white-space: nowrap;
}

.schedule-panel--inspectors.schedule-ctx--embedded > .inspector-list,
.schedule-panel--inspectors.schedule-ctx--embedded .inspector-body > .inspector-list {
  flex: 1;
  min-height: 0;
  margin: 0;
  padding: 20px 0 17px 0;
  overflow-x: hidden;
  overflow-y: auto;
  box-sizing: border-box;
}

.schedule-panel--inspectors.schedule-ctx--embedded .inspector-row {
  box-sizing: border-box;
  width: 220px;
  height: 42px;
  min-height: 42px;
  max-height: 42px;
  margin: 0 16px;
  padding: 0 111px 0 7px;
  border: none;
  background: transparent;
  align-items: center;
  gap: 6px;
  color: #ffffff;
  overflow: hidden;
}

.schedule-panel--inspectors.schedule-ctx--embedded .inspector-text {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  flex: 1;
  min-width: 0;
  gap: 2px;
  margin: 0;
  padding: 0;
}

.schedule-panel--inspectors.schedule-ctx--embedded .inspector-name {
  display: block;
  width: 100%;
  margin: 0;
  padding: 0;
  font-family: SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui, sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: 16px;
  letter-spacing: 0;
  color: #ffffff;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.schedule-panel--inspectors.schedule-ctx--embedded .inspector-orders {
  display: block;
  width: 100%;
  margin: 0;
  padding: 0;
  font-family: SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui, sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0;
  color: #cccccc;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.schedule-panel--inspectors.schedule-ctx--embedded .inspector-orders-count {
  display: inline;
  margin: 0;
  padding: 0;
  font-family: SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui, sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
  letter-spacing: 0;
  color: #cccccc;
}

.schedule-panel--inspectors.schedule-ctx--embedded .inspector-dot {
  width: 16px;
  height: 16px;
  margin-top: 0;
  flex-shrink: 0;
  border-radius: 50%;
  display: block;
}

.schedule-panel--inspectors.schedule-ctx--embedded .inspector-chevron-ic {
  width: 8px;
  height: 5px;
  margin-top: 0;
  flex-shrink: 0;
  display: block;
  object-fit: contain;
  filter: none;
  opacity: 1;
  transform: rotate(-90deg);
}

.schedule-panel--inspectors.schedule-ctx--embedded .inspector-chevron-ic.open {
  transform: rotate(0deg);
}

.schedule-panel--inspectors.schedule-ctx--embedded .inspector-row.inspector-row--active {
  width: 220px;
  height: 42px;
  min-height: 42px;
  max-height: 42px;
  background: #ffffff;
  border-radius: 8px;
  box-shadow: none;
}

.schedule-panel--inspectors.schedule-ctx--embedded .inspector-row.inspector-row--active .inspector-name {
  color: #000000;
}

.schedule-panel--inspectors.schedule-ctx--embedded .inspector-row.inspector-row--active .inspector-orders,
.schedule-panel--inspectors.schedule-ctx--embedded .inspector-row.inspector-row--active .inspector-orders-count {
  color: #cccccc;
}

.schedule-panel--inspectors.schedule-ctx--embedded .inspector-row.inspector-row--active .inspector-chevron-ic {
  width: 8px;
  height: 5px;
  filter: none;
  opacity: 1;
}

.schedule-panel--inspectors.schedule-ctx--embedded .inspector-list .inspector-block + .inspector-block {
  margin-top: 4px;
}

.schedule-panel--inspectors.schedule-ctx--embedded .inspector-block {
  position: relative;
}

.schedule-panel--inspectors.schedule-ctx--embedded .inspector-assignments {
  margin: 0;
  padding: 6px 16px 0 16px;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-sizing: border-box;
}

.schedule-panel--inspectors.schedule-ctx--embedded .inspector-assignment {
  display: block;
  margin: 0;
  padding: 0;
  width: 100%;
  box-sizing: border-box;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.9);
}

.schedule-panel--inspectors.schedule-ctx--embedded .inspector-assignment-btn .assignment-idx {
  grid-column: 1;
  width: 16px;
  margin: 0;
  padding: 0;
  font-family: SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui, sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
  letter-spacing: 0;
  color: #cccccc;
  text-align: center;
}

.schedule-panel--inspectors.schedule-ctx--embedded .inspector-assignment-btn .assignment-pin {
  grid-column: 2;
  grid-row: 1;
  width: 16px;
  height: 16px;
  margin: 0;
  justify-self: start;
  align-self: start;
  display: block;
  -webkit-mask-size: contain;
  mask-size: contain;
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  -webkit-mask-position: center;
  mask-position: center;
}

.schedule-panel--inspectors.schedule-ctx--embedded .inspector-assignment-btn .assignment-body {
  grid-column: 3;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.schedule-panel--inspectors.schedule-ctx--embedded .assignment-addr {
  display: block;
  font-family: SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui, sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: 16px;
  letter-spacing: 0;
  color: #ffffff;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.schedule-panel--inspectors.schedule-ctx--embedded .assignment-id {
  display: block;
  font-family: SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui, sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0;
  color: #cccccc;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: normal;
}

.schedule-panel--inspectors.schedule-ctx--embedded .assignment-time {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 100%;
  font-family: SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui, sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0;
  color: #cccccc;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.schedule-panel--inspectors.schedule-ctx--embedded .assignment-time-ic {
  width: 12px;
  height: 12px;
  flex-shrink: 0;
  display: block;
  object-fit: contain;
}

.schedule-panel--inspectors.schedule-ctx--embedded .inspector-assignment-btn {
  display: grid;
  grid-template-columns: 16px 16px minmax(0, 1fr);
  column-gap: 8px;
  align-items: start;
  width: 100%;
  margin: 0 0 0 21px;
  padding: 0;
  border: none;
  background: transparent;
  text-align: left;
  cursor: pointer;
  color: inherit;
  font: inherit;
  box-sizing: border-box;
}

.schedule-panel--inspectors.schedule-ctx--embedded.schedule-ctx--reschedule .assignment-addr {
  width: auto;
  max-width: 100%;
  height: auto;
  min-height: 16px;
  max-height: none;
  white-space: normal;
  overflow: visible;
  text-overflow: clip;
  overflow-wrap: break-word;
  word-break: break-word;
}

.schedule-panel--inspectors.schedule-ctx--embedded .inspector-assignment--active {
  box-sizing: border-box;
  width: 220px;
  height: auto;
  min-height: 0;
  max-height: none;
  margin: 0;
  padding: 5px 8px 4px 0;
  background: #ffffff;
  border-radius: 8px;
  overflow: hidden;
}

.schedule-panel--inspectors.schedule-ctx--embedded .inspector-assignment--active .inspector-assignment-btn {
  margin: 0 0 0 21px;
  width: calc(100% - 21px);
  max-width: calc(100% - 21px);
  box-sizing: border-box;
}

.schedule-panel--inspectors.schedule-ctx--embedded .inspector-assignment--active .assignment-body {
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
}

.schedule-panel--inspectors.schedule-ctx--embedded .inspector-assignment--active .assignment-addr {
  width: 100%;
  max-width: 100%;
  height: auto;
  min-height: 16px;
  max-height: none;
  color: #000000;
  white-space: normal;
  overflow: hidden;
  text-overflow: clip;
  overflow-wrap: break-word;
  word-break: break-word;
}

.schedule-panel--inspectors.schedule-ctx--embedded .inspector-assignment--active .assignment-id,
.schedule-panel--inspectors.schedule-ctx--embedded .inspector-assignment--active .assignment-time {
  color: #666666;
}

.schedule-panel--inspectors.schedule-ctx--embedded .inspector-assignment--active .assignment-idx {
  color: rgba(0, 0, 0, 0.55);
}

.schedule-panel--inspectors {
  overflow-x: hidden;
  overflow-y: auto;
  background: #3d3d3d;
  color: #ffffff;
  padding: 20px 16px;
}

.inspector-body {
  position: relative;
  min-height: 0;
}

.schedule-panel--inspectors.schedule-ctx--embedded .inspector-body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.inspector-body-loading {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(72, 72, 72, 0.72);
}

.inspector-body-loading :deep(.hm-loading) {
  padding: 0;
}

.inspector-list--loading {
  opacity: 0.45;
  pointer-events: none;
}

.panel-heading {
  margin: 0 0 16px;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: rgba(255, 255, 255, 0.85);
}

.inspector-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.inspector-row {
  width: 100%;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  border: none;
  border-radius: 12px;
  background: transparent;
  color: #ffffff;
  text-align: left;
  cursor: pointer;
  box-sizing: border-box;
}

.inspector-row--active {
  background: #ffffff;
  border-radius: 8px;
  box-shadow: none;
}

.inspector-row--active .inspector-name {
  color: #000000;
}

.inspector-row--active .inspector-orders {
  color: #cccccc;
}

.inspector-row--active .inspector-chevron-ic {
  width: 8px;
  height: 5px;
  filter: none;
  opacity: 1;
}

.inspector-chevron-ic {
  width: 10px;
  height: 10px;
  flex-shrink: 0;
  margin-top: 4px;
  display: block;
  object-fit: contain;
  transform: rotate(-90deg);
  transition: transform 0.15s ease;
  filter: brightness(0) invert(1);
  opacity: 0.75;
}

.inspector-chevron-ic.open {
  transform: rotate(0deg);
}

.inspector-dot {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  margin-top: 0;
  display: block;
  border-radius: 50%;
}

.inspector-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.inspector-name {
  font-size: 14px;
  font-weight: 600;
  line-height: 1.25;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.inspector-orders {
  font-size: 12px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.65);
  line-height: 1.2;
}

.schedule-panel--inspectors.schedule-ctx--reschedule .assignment-addr {
  width: auto;
  max-width: 100%;
  height: auto;
  min-height: 16px;
  max-height: none;
  white-space: normal;
  overflow: visible;
  text-overflow: clip;
  overflow-wrap: break-word;
  word-break: break-word;
}

.schedule-panel--inspectors .inspector-assignment--active {
  box-sizing: border-box;
  width: 220px;
  height: auto;
  min-height: 0;
  max-height: none;
  padding: 5px 8px 4px;
  background: #ffffff;
  border-radius: 8px;
  overflow: hidden;
}

.schedule-panel--inspectors .inspector-assignment--active .inspector-assignment-btn {
  margin: 0;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.schedule-panel--inspectors .inspector-assignment--active .assignment-body {
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
}

.schedule-panel--inspectors .inspector-assignment--active .assignment-addr {
  width: 100%;
  max-width: 100%;
  height: auto;
  min-height: 16px;
  max-height: none;
  color: #000000;
  white-space: normal;
  overflow: hidden;
  text-overflow: clip;
  overflow-wrap: break-word;
  word-break: break-word;
}

.inspector-assignments {
  list-style: none;
  margin: 8px 0 4px 28px;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.inspector-assignment {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.9);
}

.inspector-assignment--readonly .inspector-assignment-btn {
  cursor: default;
  opacity: 0.85;
}

.inspector-assignment--readonly .inspector-assignment-btn:disabled {
  pointer-events: none;
}

.inspector-assignment-btn {
  display: grid;
  grid-template-columns: 16px 16px minmax(0, 1fr);
  column-gap: 8px;
  align-items: start;
  width: 100%;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  text-align: left;
  cursor: pointer;
  color: inherit;
  font: inherit;
  box-sizing: border-box;
}

.assignment-idx {
  flex-shrink: 0;
  width: 16px;
  margin-top: 2px;
  font-size: 12px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.55);
  text-align: right;
}

.assignment-pin {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  margin-top: 0;
  display: block;
  -webkit-mask-size: contain;
  mask-size: contain;
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  -webkit-mask-position: center;
  mask-position: center;
}

.assignment-body {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.assignment-addr {
  font-size: 13px;
  font-weight: 600;
  line-height: 1.25;
}

.assignment-id {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.65);
  word-break: break-all;
}

.assignment-time {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 100%;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.65);
  white-space: nowrap;
}

.assignment-time-ic {
  width: 12px;
  height: 12px;
  flex-shrink: 0;
  display: block;
  object-fit: contain;
}
</style>
